/**
 * @fileoverview Job Detail Layout
 *
 * Purpose: Layout for a single job's detail view, including header with
 * key info and phase navigation tabs.
 * URL: /dashboard/jobs/:jobId
 * Protection: Requires authentication + job access
 *
 * Features:
 * - Job header with entity name, status, type, dates
 * - Phase tabs navigation (Plan → Gather → Review → Findings → Reporting → Recoveries)
 * - Renders child phase routes via Outlet
 *
 * Dependencies:
 * - Convex: jobs.queries.getJob
 * - shadcn Tabs, Badge, Button
 */

import { createFileRoute, Outlet, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Loader2, AlertCircle } from "lucide-react";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId",
)({
  component: JobDetailLayout,
  beforeLoad: () => ({
    headerTitle: "Job Detail",
  }),
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function JobDetailLayout() {
  const { jobId } = Route.useParams();

  const { data: job, isLoading } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );

  if (isLoading || job === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-16">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <div className="text-center">
          <h2 className="text-lg font-medium">Job Not Found</h2>
          <p className="text-sm text-muted-foreground">
            This job doesn't exist or you don't have access to it.
          </p>
        </div>
        <Link
          to="/dashboard/jobs"
          className="text-sm text-primary underline-offset-4 hover:underline"
        >
          ← Back to Jobs
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="flex min-w-0 flex-1 flex-col">
        <Outlet />
      </div>
    </div>
  );
}
